import { PropertiesPanelPropertyConfig } from '../../../../../widgets/propertiesPanel';
import { T } from 'src/shared/helpers';

export type LightConfigHelpers = {
    updateProperty: <K extends keyof LightNode>(key: K, value: LightNode[K]) => void;
    updateVector: (vectorType: 'position' | 'direction', axis: 'x' | 'y' | 'z', value: number) => void;
    handleColorChange: (key: 'diffuseColor' | 'specularColor' | 'groundColor') => (_: any, color: string) => void;
};

export type LightNode = {
    id: string;
    name: string;
    lightType: string;
    intensity: number;
    diffuseColor: string;
    specularColor: string;
    groundColor?: string;
    position?: [number, number, number];
    direction?: [number, number, number];
    angle?: number;
    exponent?: number;
};

const createPropertyConfig = (config: PropertiesPanelPropertyConfig) => config;

// Utility per normalizzare colori hex
const normalizeHexColor = (color: string): string => {
    if (!color) return '#FFFFFF';

    // Se non inizia con #, aggiungilo
    if (!color.startsWith('#')) {
        color = '#' + color;
    }

    // Se non è valido, ritorna bianco
    if (!/^#[0-9A-F]{6}$/i.test(color)) {
        console.warn('Invalid hex color:', color, 'using white as fallback');
        return '#FFFFFF';
    }

    // Assicurati che sia maiuscolo
    return color.toUpperCase();
};

// Configurazione per un input triplo (x, y, z) per vettori
const createVectorConfig = (
    id: string,
    label: string,
    values: [number, number, number],
    onChange: (axis: 'x' | 'y' | 'z', value: number) => void
): PropertiesPanelPropertyConfig => {
    return createPropertyConfig({
        type: 'point',
        id,
        label,
        coords: [
            {
                id: 'x',
                label: 'X',
                value: values[0],
                onChange: (newValue) => onChange('x', newValue)
            },
            {
                id: 'y',
                label: 'Y',
                value: values[1],
                onChange: (newValue) => onChange('y', newValue)
            },
            {
                id: 'z',
                label: 'Z',
                value: values[2],
                onChange: (newValue) => onChange('z', newValue)
            }
        ]
    });
};

// Configurazione per il gruppo delle proprietà di base delle luci
const createBasicLightPropertiesGroup = (light: LightNode, helpers: LightConfigHelpers): PropertiesPanelPropertyConfig => {
    return createPropertyConfig({
        type: 'group',
        id: 'basic-light-properties',
        header: T._('Basic Properties', 'Admin'),
        defaultIsOpen: true,
        properties: [
            // Nome
            createPropertyConfig({
                type: 'text',
                id: 'name',
                label: T._('Name', 'Admin'),
                value: light.name || 'Unnamed Light',
                onChange: (newValue) => {
                    const validatedName = (newValue || '').trim();
                    helpers.updateProperty('name', validatedName);
                }
            }),

            // Intensità
            createPropertyConfig({
                type: 'range',
                id: 'intensity',
                label: T._('Intensity', 'Admin'),
                value: typeof light.intensity === 'number' ? light.intensity : 1,
                min: 0.1,
                max: 10,
                step: 0.1,
                onChange: (value) => helpers.updateProperty('intensity', value)
            }),

            // Colore diffuso
            createPropertyConfig({
                type: 'color',
                id: 'diffuseColor',
                label: T._('Diffuse Color', 'Admin'),
                value: normalizeHexColor(light.diffuseColor),
                onChange: (value) => helpers.handleColorChange('diffuseColor')(null, value)
            }),

            // Colore speculare
            createPropertyConfig({
                type: 'color',
                id: 'specularColor',
                label: T._('Specular Color', 'Admin'),
                value: normalizeHexColor(light.specularColor),
                onChange: (value) => helpers.handleColorChange('specularColor')(null, value)
            })
        ]
    });
};

// Configurazione per il gruppo delle proprietà specifiche per tipo di luce
const createLightTypePropertiesGroup = (light: LightNode, helpers: LightConfigHelpers): PropertiesPanelPropertyConfig | null => {
    const properties: PropertiesPanelPropertyConfig[] = [];

    switch (light.lightType) {
        case 'point_light':
            properties.push(
                createVectorConfig('position', T._('Position', 'Admin'), light.position || [0, 0, 0], (axis, value) => {
                    helpers.updateVector('position', axis, value);
                })
            );
            break;

        case 'directional_light':
            properties.push(
                createVectorConfig('direction', T._('Direction', 'Admin'), light.direction || [0, -1, 0], (axis, value) => {
                    helpers.updateVector('direction', axis, value);
                })
            );
            break;

        case 'spot_light':
            properties.push(
                createVectorConfig('position', T._('Position', 'Admin'), light.position || [0, 0, 0], (axis, value) => {
                    helpers.updateVector('position', axis, value);
                }),
                createVectorConfig('direction', T._('Direction', 'Admin'), light.direction || [0, -1, 0], (axis, value) => {
                    helpers.updateVector('direction', axis, value);
                }),
                createPropertyConfig({
                    type: 'range',
                    id: 'exponent',
                    label: T._('Exponent', 'Admin'),
                    value: typeof light.exponent === 'number' ? light.exponent : 1,
                    min: 1,
                    max: 128,
                    step: 1,
                    onChange: (value) => helpers.updateProperty('exponent', value)
                }),
                createPropertyConfig({
                    type: 'range',
                    id: 'angle',
                    label: T._('Angle (degrees)', 'Admin'),
                    value: typeof light.angle === 'number' ? light.angle : 30,
                    min: 0,
                    max: 180,
                    step: 0.1,
                    onChange: (value) => helpers.updateProperty('angle', value)
                })
            );
            break;

        case 'hemispheric_light':
            properties.push(
                createPropertyConfig({
                    type: 'color',
                    id: 'groundColor',
                    label: T._('Ground Color', 'Admin'),
                    value: normalizeHexColor(light.groundColor || '#000000'),
                    onChange: (value) => helpers.handleColorChange('groundColor')(null, value)
                })
            );
            break;

        default:
            return null;
    }

    if (properties.length === 0) return null;

    return createPropertyConfig({
        type: 'group',
        id: 'light-type-properties',
        header: T._('Light Type Properties', 'Admin'),
        defaultIsOpen: true,
        properties
    });
};

// Funzione principale che determina quale configurazione usare per le luci
const lightPropertiesByType = (light: LightNode, helpers: LightConfigHelpers): PropertiesPanelPropertyConfig[] => {
    const baseProperties = [createBasicLightPropertiesGroup(light, helpers), createLightTypePropertiesGroup(light, helpers)];

    return baseProperties.filter(Boolean) as PropertiesPanelPropertyConfig[];
};

// Hook principale da esportare (simile a useObjectPropertiesCreator)
export const useLightPropertiesCreator = (): ((light: LightNode, helpers: LightConfigHelpers) => PropertiesPanelPropertyConfig[]) => {
    return (light: LightNode, helpers: LightConfigHelpers) => {
        return lightPropertiesByType(light, helpers);
    };
};
